import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, Phone } from 'lucide-react';
import { NAV_SECTIONS, CONTACT_PHONE } from '../constants';
import { Logo } from './Logo';

export const Navbar: React.FC = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const scrollTo = (href: string) => {
    const element = document.querySelector(href);
    if (element) element.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <nav className="fixed w-full z-50 transition-all duration-300 bg-white/70 backdrop-blur-md border-b border-[#bdcabd]/10">
      <div className="container mx-auto px-4 max-w-7xl">
        <div className="flex justify-between items-center h-20 md:h-28">
          <a href="#/" className="flex-shrink-0 flex items-center group py-2 hover:opacity-80 transition-opacity" aria-label="ANCORA Automations">
            <Logo variant="light" />
          </a>

          <div className="hidden md:flex items-center space-x-6">
            {NAV_SECTIONS.map((link) => (
              link.href.startsWith('/') ? (
                <a
                  key={link.href}
                  href={link.href === '/blog' ? '/blog/' : link.href}
                  className="text-slate-600 hover:text-emerald-700 font-medium transition-colors text-base"
                >
                  {link.label}
                </a>
              ) : (
                <button
                  key={link.href}
                  type="button"
                  onClick={() => scrollTo(link.href)}
                  className="text-slate-600 hover:text-emerald-700 font-medium transition-colors text-base"
                >
                  {link.label}
                </button>
              )
            ))}
            <div className="pl-4 border-l border-slate-200 ml-2">
              <a
                href={`tel:${CONTACT_PHONE.replace(/\s/g, '')}`}
                className="flex items-center gap-2 text-slate-900 font-bold hover:text-emerald-700 transition-colors text-base"
              >
                <Phone size={18} className="text-secondary" />
                {CONTACT_PHONE}
              </a>
            </div>
          </div>

          <div className="hidden md:flex items-center">
            <button
              type="button"
              onClick={() => scrollTo('#contacto')}
              className="bg-[#006d3d] text-white px-5 py-2.5 rounded-lg font-medium hover:opacity-90 transition-all shadow-lg shadow-primary/20 text-base"
            >
              Contacto
            </button>
          </div>

          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="md:hidden p-2 rounded-lg transition-colors text-slate-900"
            aria-label="Abrir menú"
          >
            {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.2 }}
            className="md:hidden bg-white border-t border-slate-100 shadow-lg overflow-hidden"
          >
            <div className="container mx-auto px-4 py-4 space-y-2">
              {NAV_SECTIONS.map((link) => (
                link.href.startsWith('/') ? (
                  <a
                    key={link.href}
                    href={link.href === '/blog' ? '/blog/' : link.href}
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="block w-full text-left px-4 py-3 rounded-lg text-slate-600 hover:bg-slate-50 font-medium transition-colors"
                  >
                    {link.label}
                  </a>
                ) : (
                  <button
                    key={link.href}
                    type="button"
                    onClick={() => {
                      scrollTo(link.href);
                      setIsMobileMenuOpen(false);
                    }}
                    className="block w-full text-left px-4 py-3 rounded-lg text-slate-600 hover:bg-slate-50 font-medium transition-colors"
                  >
                    {link.label}
                  </button>
                )
              ))}
              <a
                href={`tel:${CONTACT_PHONE.replace(/\s/g, '')}`}
                className="block px-4 py-3 rounded-lg text-slate-600 hover:bg-slate-50 font-medium transition-colors"
              >
                <span className="inline-flex items-center gap-1.5">
                  <Phone size={14} />
                  {CONTACT_PHONE}
                </span>
              </a>
              <button
                type="button"
                onClick={() => {
                  scrollTo('#contacto');
                  setIsMobileMenuOpen(false);
                }}
                className="flex items-center justify-center gap-2 px-4 py-3 rounded-xl text-white font-bold mt-4 transition-all hover:opacity-90 bg-[#006d3d] w-full"
              >
                Contacto
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
};
